import { useFilterChoices } from "./useFilterChoices";
import { useSavedSearchDelete } from "./useSavedSearchDelete";
import { useFilterPanelContext } from "./useFilterPanelContext";
import { useFilterSummaries } from "./useFilterSummaries";
import { useSavedSearchRename } from "./useSavedSearchRename";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSavedSearches } from "../../../api/searchApi";
import { getWorkspacesApi } from "../../../api/workspaceApi";
import { useAuth } from "../../../components/AuthProvider";
import Defaults from "../../../helper/defaults";

import { ALL_COMPANIES_PIN_ID, getDefaultPinnedSavedSearchId, getPinnedSavedSearchIds, getSavedSearchPinId, sortSavedSearchesByPin, togglePinnedSavedSearchId } from "../../../helper/pinnedSavedSearches";

import { shouldShowClearFilters } from "../../../helper/savedSearchDirty";
import { mapWorkspaceToSavedSearchOption } from "../../Workspaces/savedSearchQuery";
import { getMockSavedSearches, isMockSavedSearchesEnabled } from "../../../fixture/mockSavedSearches";
import useSavedSearchSelection from "./useSavedSearchSelection";

/**
 * Shared filter orchestration for V2 horizontal bar and V3/V4 banner.
 * Parent (CompanyList) owns filter values; this hook owns choice lists,
 * saved-search UI state, summaries, and clear/delete/pin/edit actions.
 *
 * @param {"legacy" | "workspace"} savedSearchCatalog
 *   - legacy: GET /all-saved-searches/ (V1–V3)
 *   - workspace: GET /workspaces as Saved Search options (V4)
 * @param {(option) => void} [onOpenSavedSearch]
 *   Optional override. When set, selecting a saved search calls this instead
 *   of hydrating filters (legacy navigation hook). V4 Search leaves this unset
 *   so selection restores filters on the current page.
 */

export default function useFilterController({
  locations,
  setLocations,
  termsInclude,
  setTermsInclude,
  termsExclude,
  setTermsExclude,
  industryInclude,
  setIndustryInclude,
  industryExclude,
  setIndustryExclude,
  revenueLowerBound,
  setRevenueLowerBound,
  revenueUpperBound,
  setRevenueUpperBound,
  employeesLowerBound,
  setEmployeesLowerBound,
  employeesUpperBound,
  setEmployeesUpperBound,
  naturalQuery,
  setNaturalQuery,
  onClear,
  savedSearchesRefreshTrigger,
  isNaturalLanguageMode = false,
  savedSearchCatalog = "legacy",
  onOpenSavedSearch,
  onSavedSearchBaselineChange,
  onCommitFilters,
  onDiscardFilters,
  onHydrateFilters,
  hasUncommittedFilters = false,
  debounceMultiSelectUpdates = true,
  onInitialDefaultResolved,
  adoptSavedSearch = null,
  onAdoptSavedSearchConsumed,
  /** True when applied filters diverge from the loaded saved-search baseline. */
  isSavedSearchDirty = false,
  /**
   * Loaded saved-search query used as the Clear revert target.
   * When set, Clear restores this baseline instead of wiping filters.
   */
  savedSearchBaseline = null,
  /** Force filter chips/panels disabled. */
  filtersLocked = false,
  /**
   * When false, skip loading the pinned default onto the filter bar
   * (Saved Search detail already owns its workspace hydration).
   */
  applyPinnedDefault = true,
  /**
   * When set, open this filter chip once.
   * Parent should clear via onPendingActiveFilterConsumed.
   */
  pendingActiveFilter = null,
  onPendingActiveFilterConsumed = null,
  /** Called after a successful inline rename. */
  onEditSearchSuccess = null,
}) {
  const { token } = useAuth();
  const [activeFilter, setActiveFilter] = useState(null);
  const { industryChoices, locationChoices } = useFilterChoices(token);
  const [savedSearchChoices, setSavedSearchChoices] = useState([]);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pinnedIds, setPinnedIds] = useState(() => getPinnedSavedSearchIds());
  const {
    isEditingSavedSearch, editingSavedSearchTitle, setEditingSavedSearchTitle, editSnapshot,
    isSavingEdit, editSearchError, onEditSearchSuccessRef, initiateEditSearch,
    cancelEditSearch, confirmEditSearch
  } = useSavedSearchRename({
    onEditSearchSuccess, savedSearchCatalog, setSavedSearchChoices, setActiveFilter,
    setSelectedSavedSearch
  });

  const [savedSearchesLoaded, setSavedSearchesLoaded] = useState(false);
  const appliedDefaultPinRef = useRef(false);

  onEditSearchSuccessRef.current = onEditSearchSuccess;
  const onCommitFiltersRef = useRef(onCommitFilters);
  onCommitFiltersRef.current = onCommitFilters;

  useEffect(() => {
    let cancelled = false;

    const loadSavedSearches = async () => {
      try {
        if (savedSearchCatalog === "workspace") {
          const response = await getWorkspacesApi();
          if (cancelled) return;
          let rows = Array.isArray(response?.data) ? response.data : [];
          if (rows.length === 0 && isMockSavedSearchesEnabled()) {
            rows = getMockSavedSearches();
          }
          const mapped = rows.map(mapWorkspaceToSavedSearchOption).filter(Boolean);
          setSavedSearchChoices(mapped);
          setSavedSearchesLoaded(true);
          setSelectedSavedSearch((current) => {
            if (!current) return null;
            const currentId = current.id || current._id;
            return mapped.find((row) => (row.id || row._id) === currentId) || current;
          });
          return;
        }

        const response = await getSavedSearches();
        if (cancelled) return;
        setSavedSearchChoices(response?.data || []);
        setSavedSearchesLoaded(true);
      } catch (_error) {
        if (cancelled) return;
        setSavedSearchChoices([]);
        setSavedSearchesLoaded(true);
      }
    };

    setSavedSearchesLoaded(false);
    loadSavedSearches();
    return () => {
      cancelled = true;
    };
  }, [savedSearchesRefreshTrigger, token, savedSearchCatalog]);

  const hydrateFiltersFromSavedSearch = useCallback(
    (selectedSearchObject) => {
      const searchParams = selectedSearchObject?.query;
      if (!searchParams || typeof searchParams !== "object") {
        console.error("Saved search is missing query parameters");
        return;
      }

      setTermsInclude(searchParams.TermsInclude || []);
      setTermsExclude(searchParams.TermsExclude || []);
      setIndustryInclude(searchParams.IndustryInclude || []);
      setIndustryExclude(searchParams.IndustryExclude || []);
      setLocations(searchParams.Location || []);
      setRevenueLowerBound(searchParams.RevenueLowerBound ?? Defaults.search.revenueLowerBound);
      setRevenueUpperBound(searchParams.RevenueUpperBound ?? Defaults.search.revenueUpperBound);
      setEmployeesLowerBound(searchParams.EmployeesLowerBound ?? Defaults.search.employeesLowerBound);
      setEmployeesUpperBound(searchParams.EmployeesUpperBound ?? Defaults.search.employeesUpperBound);
      setNaturalQuery(searchParams.NaturalLanguageQuery || "");
      onHydrateFilters?.(searchParams);
    },
    [
      setTermsInclude,
      setTermsExclude,
      setIndustryInclude,
      setIndustryExclude,
      setLocations,
      setRevenueLowerBound,
      setRevenueUpperBound,
      setEmployeesLowerBound,
      setEmployeesUpperBound,
      setNaturalQuery,
      onHydrateFilters,
    ]
  );

  const { selectSavedSearch, invalidateSavedSearch, savedSearchLoad, retrySavedSearch } = useSavedSearchSelection({
    savedSearchCatalog,
    onResolved: (resolved) => {
      setSavedSearchChoices((prev) => prev.map((row) =>
        (row.id || row._id) === (resolved.id || resolved._id) ? { ...row, ...resolved } : row
      ));
      setSelectedSavedSearch(resolved);
      hydrateFiltersFromSavedSearch(resolved);
      onSavedSearchBaselineChange?.(resolved.query, resolved);
      onInitialDefaultResolved?.();
    },
  });

  const handleSelectSavedSearch = useCallback(async (_event, option) => {
    appliedDefaultPinRef.current = true;
    if (!option || typeof onOpenSavedSearch === "function") {
      invalidateSavedSearch();
      setSelectedSavedSearch(option || null);
      if (option) onOpenSavedSearch(option);
      else onSavedSearchBaselineChange?.(null);
      onInitialDefaultResolved?.();
      return;
    }
    await selectSavedSearch(option);
  }, [invalidateSavedSearch, onInitialDefaultResolved, onOpenSavedSearch, onSavedSearchBaselineChange, selectSavedSearch]);

  // After saving a modified search, adopt the new workspace as the active baseline.
  useEffect(() => {
    if (!adoptSavedSearch) return;
    invalidateSavedSearch();
    appliedDefaultPinRef.current = true;
    const option =
      typeof adoptSavedSearch === "object"
        ? mapWorkspaceToSavedSearchOption(adoptSavedSearch) || adoptSavedSearch
        : null;
    if (option && (option.id || option._id)) {
      setSelectedSavedSearch(option);
      if (option.query) {
        onSavedSearchBaselineChange?.(option.query, option);
      }
    }
    onAdoptSavedSearchConsumed?.();
    onInitialDefaultResolved?.();
  }, [adoptSavedSearch, invalidateSavedSearch, onAdoptSavedSearchConsumed, onInitialDefaultResolved, onSavedSearchBaselineChange]);

  const commitFilters = useCallback(() => {
    invalidateSavedSearch();
    appliedDefaultPinRef.current = true;
    window.setTimeout(() => {
      onCommitFiltersRef.current?.();
      onInitialDefaultResolved?.();
    }, 0);
  }, [invalidateSavedSearch, onInitialDefaultResolved]);
  const applyFilter = useCallback(() => {
    setActiveFilter(null);
    commitFilters();
  }, [commitFilters]);
  const closeFilter = useCallback((_event, reason) => {
    if (reason === "escapeKeyDown") onDiscardFilters?.();
    else commitFilters();
    setActiveFilter(null);
  }, [commitFilters, onDiscardFilters]);

  const toggleFilter = useCallback((filterId) => {
    setActiveFilter((current) => (current === filterId ? null : filterId));
  }, []);

  useEffect(() => {
    if (!pendingActiveFilter) return;
    setActiveFilter(pendingActiveFilter);
    onPendingActiveFilterConsumed?.();
  }, [pendingActiveFilter, onPendingActiveFilterConsumed]);

  const {
    revenueSummary, employeeSummary, summaries
  } = useFilterSummaries({
    termsInclude, termsExclude, industryInclude, industryExclude,
    locations, revenueLowerBound, revenueUpperBound, employeesLowerBound,
    employeesUpperBound, naturalQuery, selectedSavedSearch
  });

  const orderedSavedSearchChoices = useMemo(() => {
    if (savedSearchCatalog !== "workspace") return savedSearchChoices;
    return sortSavedSearchesByPin(savedSearchChoices, pinnedIds);
  }, [pinnedIds, savedSearchCatalog, savedSearchChoices]);

  // Once per Search visit: load the single pinned default (or stay on All companies).
  useEffect(() => {
    if (!applyPinnedDefault) return;
    if (savedSearchCatalog !== "workspace") return;
    if (!savedSearchesLoaded) return;
    if (appliedDefaultPinRef.current) return;
    if (adoptSavedSearch) return;

    if (selectedSavedSearch) {
      appliedDefaultPinRef.current = true;
      onInitialDefaultResolved?.();
      return;
    }

    const defaultId = getDefaultPinnedSavedSearchId(pinnedIds);
    appliedDefaultPinRef.current = true;

    if (!defaultId) {
      onInitialDefaultResolved?.();
      return;
    }

    if (defaultId === ALL_COMPANIES_PIN_ID) {
      onClear?.({ searchAllCompanies: true });
      onInitialDefaultResolved?.();
      return;
    }

    const match = savedSearchChoices.find(
      (row) => getSavedSearchPinId(row) === defaultId
    );
    if (!match) {
      onInitialDefaultResolved?.();
      return;
    }

    selectSavedSearch(match);
  }, [
    adoptSavedSearch, applyPinnedDefault, onClear, onInitialDefaultResolved,
    pinnedIds, selectSavedSearch, savedSearchCatalog, savedSearchChoices,
    savedSearchesLoaded, selectedSavedSearch,
  ]);

  const {
    initiateDelete, confirmDelete
  } = useSavedSearchDelete({
    setItemToDelete, setDeleteDialogOpen, itemToDelete, setIsDeleting,
    savedSearchCatalog, setSavedSearchChoices, setPinnedIds, selectedSavedSearch,
    setTermsInclude, setTermsExclude, setIndustryInclude, setIndustryExclude,
    setLocations, setRevenueLowerBound, setRevenueUpperBound, setEmployeesLowerBound,
    setEmployeesUpperBound, setNaturalQuery, onHydrateFilters, setSelectedSavedSearch,
    onSavedSearchBaselineChange, onClear
  });

  const handlePinSavedSearch = useCallback((event, option) => {
    event?.stopPropagation?.();
    const id = getSavedSearchPinId(option);
    if (!id) return;
    setPinnedIds(togglePinnedSavedSearchId(id));
  }, []);

  /** Enter the legacy inline shell as a rename-only mode. */

  const clearDraftFilters = useCallback(() => {
    setTermsInclude([]);
    setTermsExclude([]);
    setIndustryInclude([]);
    setIndustryExclude([]);
    setLocations([]);
    setRevenueLowerBound(Defaults.search.revenueLowerBound);
    setRevenueUpperBound(Defaults.search.revenueUpperBound);
    setEmployeesLowerBound(Defaults.search.employeesLowerBound);
    setEmployeesUpperBound(Defaults.search.employeesUpperBound);
    setNaturalQuery("");
  }, [
    setTermsInclude,
    setTermsExclude,
    setIndustryInclude,
    setIndustryExclude,
    setLocations,
    setRevenueLowerBound,
    setRevenueUpperBound,
    setEmployeesLowerBound,
    setEmployeesUpperBound,
    setNaturalQuery,
  ]);

  const clearAll = useCallback((options = {}) => {
    invalidateSavedSearch();
    appliedDefaultPinRef.current = true;
    onInitialDefaultResolved?.();
    clearDraftFilters();
    onHydrateFilters?.({});
    setSelectedSavedSearch(null);
    onSavedSearchBaselineChange?.(null);
    onClear?.(options);
  }, [
    invalidateSavedSearch,
    onInitialDefaultResolved,
    clearDraftFilters,
    onHydrateFilters,
    onClear,
    onSavedSearchBaselineChange,
  ]);

  // Toolbar Clear: revert to the loaded saved-search baseline when present;
  // otherwise wipe (same as selecting "All companies").
  const clearFilters = useCallback(() => {
    invalidateSavedSearch();
    if (savedSearchBaseline && typeof savedSearchBaseline === "object") {
      hydrateFiltersFromSavedSearch({ query: savedSearchBaseline });
      onClear?.();
      return;
    }
    clearAll();
  }, [invalidateSavedSearch, clearAll, hydrateFiltersFromSavedSearch, onClear, savedSearchBaseline]);

  const hasAnyManualFilters = Boolean(
    termsInclude?.length ||
      termsExclude?.length ||
      locations?.length ||
      industryInclude?.length ||
      industryExclude?.length ||
      revenueSummary.isActive ||
      employeeSummary.isActive
  );

  const showClearFilters = shouldShowClearFilters({
    hasAnyManualFilters,
    hasSavedBaseline: Boolean(
      savedSearchBaseline && typeof savedSearchBaseline === "object"
    ),
    isDirty: Boolean(isSavedSearchDirty),
  });

  const filtersDisabled =
    Boolean(isNaturalLanguageMode) ||
    Boolean(filtersLocked) ||
    Boolean(isEditingSavedSearch);
  const rowActions = savedSearchCatalog === "workspace" ? "pin-edit" : "delete";
  const originalEditTitle = editSnapshot?.title ? String(editSnapshot.title) : "";
  const isEditTitleDirty =
    isEditingSavedSearch &&
    editingSavedSearchTitle.trim() !== originalEditTitle.trim();
  const canSaveEditSearch =
    isEditingSavedSearch &&
    Boolean(editingSavedSearchTitle.trim()) &&
    isEditTitleDirty;

  const {
    panelContext
  } = useFilterPanelContext({
    filtersDisabled, debounceMultiSelectUpdates, applyFilter, termsInclude,
    setTermsInclude, termsExclude, setTermsExclude, industryChoices,
    industryInclude, setIndustryInclude, industryExclude, setIndustryExclude,
    locations, setLocations, locationChoices, revenueLowerBound,
    revenueUpperBound, setRevenueLowerBound, setRevenueUpperBound, employeesLowerBound,
    employeesUpperBound, setEmployeesLowerBound, setEmployeesUpperBound, naturalQuery,
    setNaturalQuery, orderedSavedSearchChoices, selectedSavedSearch, handleSelectSavedSearch,
    initiateDelete, handlePinSavedSearch, initiateEditSearch, pinnedIds,
    rowActions, savedSearchCatalog, isEditingSavedSearch
  });

  return {
    activeFilter,
    setActiveFilter,
    toggleFilter,
    closeFilter,
    commitFilters,
    discardFilters: onDiscardFilters,
    hasUncommittedFilters,
    summaries,
    hasAnyManualFilters,
    showClearFilters,
    filtersDisabled,
    clearDraftFilters,
    clearAll,
    clearFilters,
    panelContext,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    setItemToDelete,
    confirmDelete,
    isDeleting,
    pinnedIds,
    savedSearchCatalog,
    isSavedSearchDirty: Boolean(isSavedSearchDirty),
    initiateEditSearch,
    cancelEditSearch,
    confirmEditSearch,
    isEditingSavedSearch,
    editingSavedSearchTitle,
    setEditingSavedSearchTitle,
    isSavingEdit,
    editSearchError,
    savedSearchLoad,
    retrySavedSearch,
    canSaveEditSearch,
  };
}
